// The two feedback sections for one submitted LNAT Section B essay.
//
// Modelled directly on TaraWritingFeedbackPanel.js, at two client-facing
// stages instead of three: mastery_scoring runs inline on the backend right
// after reasoning_trace succeeds and is never a stage this panel starts or
// polls for on its own -- by the time a poll would see it, it has already
// finished (see lnat_scoring_service.py's module docstring).
//
// The only component here that fetches. Everything below it takes props.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import {
  LNAT_FEEDBACK_STAGES, LNAT_STAGE_TITLES, lnatFeedbackGating,
} from '../utils/lnatFeedbackGating';
import { lnatFeedbackFromAttempt } from '../utils/lnatFeedbackFromAttempt';
import { taraFeedbackErrorCopy } from '../utils/taraFeedbackErrorCopy';
import { lnatFeedbackProgressCopy } from '../utils/lnatFeedbackProgressCopy';
import TaraFeedbackSection from './TaraFeedbackSection';
import LnatReasoningTraceFeedback from './LnatReasoningTraceFeedback';
import LnatExaminerReport from './LnatExaminerReport';
import './TaraWritingFeedbackPanel.css';
import {
  LNAT_FEEDBACK_IN_FLIGHT_KEY,
  readUserSession,
  removeUserSession,
  writeUserSession,
} from '../utils/paperProgressCache';
import { useAuth } from '../../auth/hooks/useAuth';

const RUN_LABELS = {
  reasoning_trace: 'Run reasoning trace',
  examiner_report: 'Build examiner report',
};

const POLL_MS = 4000;
const MAX_POLL_MS = 8 * 60 * 1000;
const RESUME_KEY = LNAT_FEEDBACK_IN_FLIGHT_KEY;

const EMPTY = { phase: 'idle', data: null, error: null, startedAt: null };

export default function LnatSectionBFeedbackPanel({
  attemptId, questionDbId, onToast, unlocked = true, onRequestUpgrade,
}) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [stages, setStages] = useState(() => ({
    reasoning_trace: { ...EMPTY }, examiner_report: { ...EMPTY },
  }));
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const cancelled = useRef(false);
  const polling = useRef(null);

  const read = useCallback(async () => {
    const payload = await quizGeneratorAPI.getLnatSectionBFeedback(attemptId, questionDbId);
    const next = lnatFeedbackFromAttempt(payload);
    next.warnings.forEach((w) => console.warn(`LNAT Section B feedback: ${w}`));
    return next;
  }, [attemptId, questionDbId]);

  useEffect(() => {
    cancelled.current = false;
    if (!attemptId || !unlocked) { setLoading(false); return undefined; }

    (async () => {
      try {
        const next = await read();
        if (cancelled.current) return;
        setStages(next.stages);
        resumeInFlight(userId, next.stages, setStages);
      } catch (err) {
        if (cancelled.current) return;
        const copy = taraFeedbackErrorCopy(err);
        if (copy) {
          setStages((s) => ({
            ...s,
            reasoning_trace: { ...s.reasoning_trace, phase: 'error', error: copy },
          }));
        }
      } finally {
        if (!cancelled.current) setLoading(false);
      }
    })();

    return () => { cancelled.current = true; };
  }, [attemptId, unlocked, read, userId]);

  const anyRunning = LNAT_FEEDBACK_STAGES.some((s) => stages[s].phase === 'running');

  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(id);
  }, [anyRunning]);

  useEffect(() => {
    if (!anyRunning) {
      if (polling.current) { clearInterval(polling.current); polling.current = null; }
      return undefined;
    }
    const startedAt = Date.now();
    polling.current = setInterval(async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(polling.current);
        polling.current = null;
        return;
      }
      try {
        const next = await read();
        if (cancelled.current) return;
        setStages(next.stages);
      } catch (err) {
        console.warn('LNAT Section B feedback poll failed', err);
      }
    }, POLL_MS);
    return () => {
      if (polling.current) { clearInterval(polling.current); polling.current = null; }
    };
  }, [anyRunning, read]);

  const run = useCallback(async (stage) => {
    setStages((s) => ({
      ...s,
      [stage]: { ...s[stage], phase: 'running', error: null, startedAt: Date.now() },
    }));
    markInFlight(userId, attemptId, stage);

    try {
      const existing = await read();
      if (existing.stages[stage].phase === 'ready') {
        if (cancelled.current) return;
        setStages(existing.stages);
        clearInFlight(userId);
        return;
      }

      await quizGeneratorAPI.runLnatSectionBStage(attemptId, stage, questionDbId);
      const next = await read();
      if (cancelled.current) return;
      setStages(next.stages);
      if (next.stages[stage].phase !== 'running') clearInFlight(userId);
    } catch (err) {
      clearInFlight(userId);
      if (cancelled.current) return;
      const copy = taraFeedbackErrorCopy(err);
      if (!copy) return;
      if (!copy.retryable && onToast) onToast(copy.message);
      setStages((s) => ({ ...s, [stage]: { ...s[stage], phase: 'error', error: copy } }));
    }
  }, [attemptId, questionDbId, read, onToast, userId]);

  if (!unlocked) {
    return (
      <div className="tara-fb-stack">
        {LNAT_FEEDBACK_STAGES.map((stage) => (
          <TaraFeedbackSection
            key={stage}
            title={LNAT_STAGE_TITLES[stage]}
            phase="locked"
            lockReason="Section B essay feedback comes with this paper's plan."
          />
        ))}
        <button
          type="button"
          className="tara-ws-start-btn"
          onClick={() => onRequestUpgrade?.()}
        >
          See plans
        </button>
      </div>
    );
  }

  if (loading) {
    return <p className="tara-fb-loading">Loading your feedback…</p>;
  }

  const gating = lnatFeedbackGating(stages);
  const renderers = {
    reasoning_trace: LnatReasoningTraceFeedback,
    examiner_report: LnatExaminerReport,
  };

  return (
    <div className="tara-fb-stack">
      {LNAT_FEEDBACK_STAGES.map((stage) => {
        const entry = stages[stage];
        const { phase, lockReason } = gating[stage];
        const Renderer = renderers[stage];
        return (
          <TaraFeedbackSection
            key={stage}
            title={LNAT_STAGE_TITLES[stage]}
            phase={phase}
            statusLabel={null}
            lockReason={lockReason}
            progress={lnatFeedbackProgressCopy(stage, now - (entry.startedAt || now))}
            error={entry.error}
            runLabel={RUN_LABELS[stage]}
            onRun={() => run(stage)}
            onRetry={() => run(stage)}
          >
            <Renderer feedback={entry.data} />
          </TaraFeedbackSection>
        );
      })}
    </div>
  );
}

// The marker is the signed-in user's (sessionStorage, under their id): a
// student signing in after another in the same tab never sees theirs.
function markInFlight(userId, attemptId, stage) {
  try {
    writeUserSession(RESUME_KEY, userId, { attemptId, stage, startedAt: Date.now() });
  } catch (e) { /* private mode */ }
}

function clearInFlight(userId) {
  try { removeUserSession(RESUME_KEY, userId); } catch (e) { /* private mode */ }
}

function resumeInFlight(userId, stages, setStages) {
  const marker = readUserSession(RESUME_KEY, userId);
  if (!marker || !marker.stage) return;
  if (Date.now() - (marker.startedAt || 0) > 3 * 60 * 1000) { clearInFlight(userId); return; }
  const entry = stages[marker.stage];
  if (!entry || entry.phase !== 'idle') { clearInFlight(userId); return; }
  setStages((s) => ({
    ...s,
    [marker.stage]: { ...s[marker.stage], phase: 'running', startedAt: marker.startedAt },
  }));
}
